import { notification } from 'antd'
import { useEffect, useState } from 'react'
import { getMutationErrorMessage } from '~app/helper/graphql-error'

/**
 * Compares two field values. Arrays are compared as multisets so a selection
 * the control happens to emit in a different order does not read as an edit.
 */
const isSameValue = ({
  left,
  right,
}: {
  left: unknown
  right: unknown
}): boolean => {
  if (!Array.isArray(left) || !Array.isArray(right)) {
    return left === right
  }
  if (left.length !== right.length) {
    return false
  }
  const unmatched = [...right]
  return left.every(item => {
    const index = unmatched.indexOf(item)
    if (index === -1) {
      return false
    }
    unmatched.splice(index, 1)
    return true
  })
}

/**
 * Whether a section's working values differ from the server values it was
 * seeded with.
 */
export const isSectionDirty = <Values extends object>({
  baseline,
  values,
}: {
  baseline: Values
  values: Values
}): boolean =>
  Object.keys({ ...baseline, ...values }).some(
    key =>
      !isSameValue({
        left: baseline[key as keyof Values],
        right: values[key as keyof Values],
      })
  )

/**
 * Thrown by an `onSubmit` that writes in more than one step and got part of
 * the way. The values that did land rebaseline the form, so the screen shows
 * what the server now holds while the failure is still reported.
 */
export class PartialSaveError extends Error {
  /**
   * The edits that did not land, re-applied over `savedValues`. Everything
   * outside it follows the server, so a field the operator cannot edit never
   * holds the section dirty.
   */
  readonly pendingValues: unknown
  /**
   * What the server holds now that part of the write succeeded.
   */
  readonly savedValues: unknown

  constructor({
    message,
    pendingValues,
    savedValues,
  }: {
    message: string
    pendingValues: unknown
    savedValues: unknown
  }) {
    super(message)
    this.name = 'PartialSaveError'
    this.pendingValues = pendingValues
    this.savedValues = savedValues
  }
}

/**
 * Reconciles a save that got part of the way. The server's answer becomes the
 * baseline and the working values alike, with only the edits that did not land
 * laid back over it — so a field the operator cannot edit never holds the
 * section dirty, and a retry sends exactly what failed.
 */
export const applyPartialSave = <Values extends object>({
  pendingValues,
  savedValues,
}: {
  pendingValues: Partial<Values>
  savedValues: Values
}): { baseline: Values; values: Values } => ({
  baseline: savedValues,
  values: { ...savedValues, ...pendingValues },
})

interface Args<Values extends object> {
  /**
   * Notification title shown when `onSubmit` rejects.
   */
  errorMessage: string
  /**
   * Called whenever the section's dirty or in-flight state changes, so an
   * owner can guard against discarding unsaved work — or against closing over
   * a write that is already on its way to the server.
   */
  onStateChange?: (args: { isDirty: boolean; isSaving: boolean }) => void
  /**
   * Writes the section. Returning the saved values rebaselines the form from
   * the server's answer instead of from what was submitted, which matters
   * whenever the server recomputes fields the operator did not touch.
   */
  onSubmit: (args: { values: Values }) => Promise<Values | undefined>
  /**
   * The values the server currently holds. Read once, when the form mounts:
   * syncing it would discard an operator's edits on any background refetch.
   * Remount the section on a customer change to reseed it, keyed on the
   * customer and the section together so sibling keys stay unique.
   */
  serverValues: Values
  /**
   * Notification title shown when the write succeeds.
   */
  successMessage: string
}

/**
 * Dirty-tracked state for one section of a form that saves on its own. Holds
 * the working values, derives `dirty` against the server values, and wraps the
 * write with loading state, notifications and the post-save rebaseline.
 */
const useSectionForm = <Values extends object>({
  errorMessage,
  onStateChange,
  onSubmit,
  serverValues,
  successMessage,
}: Args<Values>) => {
  const [baseline, setBaseline] = useState(serverValues)
  const [values, setValues] = useState(serverValues)
  const [isLoading, setIsLoading] = useState(false)
  const isDirty = isSectionDirty({ baseline, values })

  useEffect(() => {
    onStateChange?.({ isDirty, isSaving: isLoading })
  }, [isDirty, isLoading, onStateChange])

  return {
    baseline,
    isDirty,
    isLoading,
    reset: () => setValues(baseline),
    submit: async () => {
      setIsLoading(true)
      try {
        const saved = (await onSubmit({ values })) ?? values
        setBaseline(saved)
        setValues(saved)
        notification.success({
          message: successMessage,
          placement: 'bottomRight',
        })
      } catch (error) {
        if (error instanceof PartialSaveError) {
          const reconciled = applyPartialSave<Values>({
            pendingValues: error.pendingValues as Partial<Values>,
            savedValues: error.savedValues as Values,
          })
          setBaseline(reconciled.baseline)
          setValues(reconciled.values)
        }
        notification.error({
          description: getMutationErrorMessage(error),
          message: errorMessage,
          placement: 'bottomRight',
        })
      } finally {
        setIsLoading(false)
      }
    },
    updateValues: (patch: Partial<Values>) =>
      setValues(current => ({ ...current, ...patch })),
    values,
  }
}

export default useSectionForm
